/**
 * Web・スマホアプリ・API で共有するデータの形（この製品の約束事の正本）。
 * 単位: 長さは mm の整数、金額は円の整数（税抜）、角度は 0/90/180/270。
 * 座標: 原点はスペースの左上、x は右へ、y は下へ（画面と同じ向き）。DXF との変換で y を反転する。
 * ID: ULID（端末で作れる）。日時: ISO 8601（UTC）。
 */
import { z } from "zod";

export const Id = z.string().min(1).max(64);
export const IsoDate = z.string().min(10).max(40);
export const Mm = z.number().int().min(-10_000_000).max(10_000_000);
export const PositiveMm = z.number().int().min(1).max(10_000_000);
export const Yen = z.number().int().min(0).max(1_000_000_000_000);

/* ===== 利用者・ログイン ===== */
export const Role = z.enum(["admin", "master", "sales"]);
export type Role = z.infer<typeof Role>;
export const UserStatus = z.enum(["active", "disabled"]);
export const User = z.object({
  id: Id,
  email: z.string().email().max(254),
  name: z.string().min(1).max(100),
  role: Role,
  status: UserStatus,
  lastLoginAt: IsoDate.nullable(),
  /** デモの利用者（ログインなしで入る管理者）。AI に 1 日の上限があり、AI の接続の設定は見られない */
  demo: z.boolean().optional(),
});
export type User = z.infer<typeof User>;

/** ログインなしで入る（POST /api/auth/demo）。本文は省いてもよい（client の既定は web） */
export const DemoSessionRequest = z.object({
  client: z.enum(["web", "ios", "android"]).optional(),
  deviceId: z.string().min(1).max(64).optional(),
});

export const Password = z.string().min(8).max(200);
export const LoginRequest = z.object({
  email: z.string().email().max(254),
  password: z.string().min(1).max(200),
  client: z.enum(["web", "ios", "android"]),
  deviceId: z.string().min(1).max(64).optional(),
});
export const LoginResponse = z.object({
  user: User,
  /** アプリだけに返す（Web は HttpOnly の Cookie）。Authorization: Bearer で送る */
  token: z.string().optional(),
  expiresAt: IsoDate,
  /** オフラインで使える期限（最後の同期から N 日） */
  offlineGraceDays: z.number().int().min(1).max(90),
});
export const CreateUserRequest = z.object({
  email: z.string().email().max(254),
  name: z.string().min(1).max(100),
  role: Role,
  /** 仮パスワード（任意）。省くとログインの仕組み（Cognito）が作ってメールで送る。本人は初回のログインで変える */
  password: Password.optional(),
});

/* ===== 登録・確認コード・パスワード再設定（ログインの仕組みは AWS Cognito） ===== */
export const ConfirmationCode = z
  .string()
  .trim()
  .regex(/^[0-9]{4,10}$/);
export const SignupRequest = z.object({ email: z.string().email().max(254), name: z.string().min(1).max(100), password: Password });
export const SignupResponse = z.object({ next: z.literal("confirm"), destination: z.string().nullable() });
export const ConfirmSignupRequest = z.object({ email: z.string().email().max(254), code: ConfirmationCode });
export const EmailRequest = z.object({ email: z.string().email().max(254) });
export const ResetPasswordRequest = z.object({ email: z.string().email().max(254), code: ConfirmationCode, newPassword: Password });
/** 管理者が作った利用者の初回ログイン。ログインの応答の challenge と session をそのまま返す */
export const NewPasswordRequest = z.object({
  email: z.string().email().max(254),
  session: z.string().min(1).max(4000),
  newPassword: Password,
  client: z.enum(["web", "ios", "android"]),
  deviceId: z.string().min(1).max(64).optional(),
});
/** ログインの応答のうち、続けて新しいパスワードを決めてもらうもの（POST /api/auth/new-password へ） */
export const LoginChallengeResponse = z.object({ challenge: z.literal("NEW_PASSWORD_REQUIRED"), session: z.string(), email: z.string() });
export const UpdateUserRequest = z.object({
  name: z.string().min(1).max(100).optional(),
  role: Role.optional(),
  status: UserStatus.optional(),
  password: Password.optional(),
});
export const ChangePasswordRequest = z.object({ currentPassword: z.string().min(1).max(200), newPassword: Password });

/* ===== マスタ ===== */
export const Maker = z.object({ id: Id, name: z.string().min(1).max(200), url: z.string().url().max(2000).nullable(), updatedAt: IsoDate });
export type Maker = z.infer<typeof Maker>;

export const Process = z.object({ id: Id, name: z.string().min(1).max(100), sortOrder: z.number().int(), updatedAt: IsoDate });
export type Process = z.infer<typeof Process>;

export const Side = z.enum(["left", "right", "front", "back"]);
export type Side = z.infer<typeof Side>;
export const ProductCategory = z.enum(["equipment", "conveyor", "auxiliary"]);

/** 値の出どころ（確認は任意。docs/catalog-import.md） */
export const FieldMeta = z.object({
  source: z.enum(["ai", "manual", "import"]),
  confidence: z.enum(["high", "medium", "low"]).optional(),
  /** 例: "digits"（桁違いの疑い）, "zero", "negative", "changed"（前回と大きく違う）, "tax_unknown" */
  warning: z.string().max(40).optional(),
  checked: z.boolean(),
  /** false のとき、その値は使わない（空と同じ扱い） */
  usable: z.boolean(),
  importRowId: Id.optional(),
});
export type FieldMeta = z.infer<typeof FieldMeta>;
export const CheckStatus = z.enum(["ai", "partial", "checked", "manual"]);

export const Clearance = z.object({ front: Mm.min(0), back: Mm.min(0), left: Mm.min(0), right: Mm.min(0) });
export type Clearance = z.infer<typeof Clearance>;

export const Product = z.object({
  id: Id,
  code: z.string().min(1).max(100),
  name: z.string().min(1).max(200),
  makerId: Id.nullable(),
  modelNo: z.string().max(200).nullable(),
  processIds: z.array(Id).max(50),
  category: ProductCategory,
  /** 流れ方向の長さ（設置時に投入口→排出口の向き） */
  widthMm: PositiveMm.nullable(),
  /** 流れと直角の奥行 */
  depthMm: PositiveMm.nullable(),
  heightMm: PositiveMm.nullable(),
  clearance: Clearance,
  inletSide: Side,
  outletSide: Side,
  listPrice: Yen.nullable(),
  /** 管理者とマスタ担当にだけ返す。営業には返さない */
  costPrice: Yen.nullable().optional(),
  installCost: Yen.nullable(),
  weightKg: z.number().min(0).max(1_000_000).nullable(),
  powerKw: z.number().min(0).max(100_000).nullable(),
  spec: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])),
  sourceUrl: z.string().url().max(2000).nullable(),
  footprintDxfKey: z.string().max(500).nullable(),
  modelGlbKey: z.string().max(500).nullable(),
  status: z.enum(["published", "archived"]),
  checkStatus: CheckStatus,
  /** キーは列名（widthMm, listPrice など） */
  fieldMeta: z.record(z.string(), FieldMeta),
  version: z.number().int().min(1),
  updatedAt: IsoDate,
});
export type Product = z.infer<typeof Product>;
export const ProductInput = Product.omit({ id: true, version: true, updatedAt: true, checkStatus: true, fieldMeta: true }).partial({
  costPrice: true,
  footprintDxfKey: true,
  modelGlbKey: true,
  status: true,
});

export const LayoutPattern = z.enum(["line", "L", "U", "ko", "auto"]);
export type LayoutPattern = z.infer<typeof LayoutPattern>;

export const Pattern = z.object({
  id: Id,
  name: z.string().min(1).max(200),
  layoutType: LayoutPattern,
  steps: z.array(z.object({ processId: Id, productId: Id.nullable(), quantity: z.number().int().min(1).max(20) })).max(100),
  note: z.string().max(2000),
  updatedAt: IsoDate,
});
export type Pattern = z.infer<typeof Pattern>;

/** 見積ルール（docs/estimate.md）。項目と率は依頼元のやり方に合わせて増減できる */
export const ExtraLineBase = z.enum(["equipment", "conveyor", "install", "equipment+conveyor", "subtotal"]);
export const EstimateRules = z.object({
  id: Id,
  name: z.string().min(1).max(100),
  isDefault: z.boolean(),
  taxRate: z.number().min(0).max(1),
  conveyorPerMeter: Yen,
  conveyorPerCorner: Yen,
  /** 据付費が設備に無いときの率（カテゴリ別） */
  installRate: z.object({ equipment: z.number().min(0).max(5), conveyor: z.number().min(0).max(5), auxiliary: z.number().min(0).max(5) }),
  /** 追加の行（「基準 × 率」か「固定額」）。上から順に計算する。subtotal はそれまでの合計 */
  extraLines: z.array(z.object({ name: z.string().min(1).max(100), base: ExtraLineBase.nullable(), rate: z.number().min(0).max(5), fixed: Yen })).max(30),
  range: z.object({ low: z.number().min(-1).max(0), high: z.number().min(0).max(5) }),
  roundTo: z.number().int().min(1).max(1_000_000),
  excludes: z.array(z.string().max(100)).max(30),
  updatedAt: IsoDate,
});
export type EstimateRules = z.infer<typeof EstimateRules>;

/* ===== スペース・レイアウト ===== */
export const Point = z.object({ x: z.number(), y: z.number() });
export type Point = z.infer<typeof Point>;
export const Polygon = z.array(Point).min(3).max(10_000);
/**
 * スペースの中にあって新しい設備を置けないもの。
 * column: 柱 / wall: 壁・吹き抜け / existing: 既存の設備・ライン / zone: 設備を置けない範囲（主通路・フォークリフトの通路・仮置き場・保全の場所など）
 * label は画面と判定の文に出す名前（例「充填ライン A」「主通路」）。heightMm は 3D 表示用（任意）
 */
export const ObstacleKind = z.enum(["column", "existing", "wall", "zone"]);
export type ObstacleKind = z.infer<typeof ObstacleKind>;
export const Obstacle = z.object({
  id: Id,
  kind: ObstacleKind,
  polygon: Polygon,
  label: z.string().max(100).optional(),
  heightMm: PositiveMm.optional(),
});
export type Obstacle = z.infer<typeof Obstacle>;
export const Door = z.object({ id: Id, a: Point, b: Point });
export type Door = z.infer<typeof Door>;

export const Space = z.object({
  /** スペースの外形（mm、時計回りでも反時計回りでもよい） */
  outline: Polygon,
  obstacles: z.array(Obstacle).max(2_000),
  doors: z.array(Door).max(200),
  source: z.enum(["manual", "dxf", "mobile_scan"]),
  /** 自動で決めた値の印（DXF の単位推定・外形の自動補修など） */
  notes: z.array(z.object({ kind: z.string().max(40), message: z.string().max(300), at: Point.optional() })).max(200),
  dxfAttachmentId: Id.nullable(),
});
export type Space = z.infer<typeof Space>;

export const Rotation = z.union([z.literal(0), z.literal(90), z.literal(180), z.literal(270)]);
export type Rotation = z.infer<typeof Rotation>;

/** x, y は回転後の設備の外形（空きを含まない）の左上の角 */
export const LayoutItem = z.object({
  id: Id,
  productId: Id,
  productVersion: z.number().int().min(1),
  processId: Id.nullable(),
  x: Mm,
  y: Mm,
  rotation: Rotation,
  mirror: z.boolean(),
  locked: z.boolean(),
});
export type LayoutItem = z.infer<typeof LayoutItem>;

export const Connector = z.object({
  id: Id,
  from: Id,
  to: Id,
  kind: z.literal("conveyor"),
  /** 通る点（mm）。長さはこの折れ線の長さ */
  path: z.array(Point).min(2).max(50),
  lengthMm: z.number().int().min(0),
  corners: z.number().int().min(0),
});
export type Connector = z.infer<typeof Connector>;

export const Layout = z.object({
  units: z.literal("mm"),
  pattern: LayoutPattern,
  aisleWidthMm: z.number().int().min(0).max(10_000),
  minGapMm: z.number().int().min(0).max(10_000),
  items: z.array(LayoutItem).max(500),
  connectors: z.array(Connector).max(1_000),
});
export type Layout = z.infer<typeof Layout>;

/* ===== 見積の結果（版に固定する） ===== */
export const ValueState = z.enum(["checked", "ai", "manual", "missing", "warning"]);
export type ValueState = z.infer<typeof ValueState>;
export const EstimateLine = z.object({
  key: z.string().max(100),
  label: z.string().max(200),
  quantity: z.number().min(0),
  unitPrice: Yen.nullable(),
  amount: Yen,
  basis: z.string().max(200).optional(),
  productId: Id.optional(),
  /** 価格の出どころ（設備の行だけ） */
  valueState: ValueState.optional(),
});
export type EstimateLine = z.infer<typeof EstimateLine>;
export const Estimate = z.object({
  lines: z.array(EstimateLine).max(1_000),
  subtotal: Yen,
  total: Yen,
  low: Yen,
  high: Yen,
  taxRate: z.number(),
  totalWithTax: Yen,
  missingPriceCount: z.number().int().min(0),
  aiValueCount: z.number().int().min(0),
  warningValueCount: z.number().int().min(0),
  rulesId: Id,
  /** 計算に使った設備の版と価格 */
  productSnapshots: z.array(z.object({ productId: Id, version: z.number().int(), listPrice: Yen.nullable(), valueState: ValueState })).max(500),
  excludes: z.array(z.string()).max(30),
  computedAt: IsoDate,
});
export type Estimate = z.infer<typeof Estimate>;

/* ===== 顧客・提案・版 ===== */
export const Customer = z.object({
  id: Id,
  name: z.string().min(1).max(200),
  industry: z.string().max(100).nullable(),
  address: z.string().max(500).nullable(),
  contactName: z.string().max(100).nullable(),
  note: z.string().max(5_000).nullable(),
  ownerUserId: Id,
  updatedAt: IsoDate,
  deletedAt: IsoDate.nullable(),
});
export type Customer = z.infer<typeof Customer>;

export const ProposalStatus = z.enum(["draft", "presented", "won", "lost"]);
export const Proposal = z.object({
  id: Id,
  customerId: Id,
  title: z.string().min(1).max(200),
  ownerUserId: Id,
  status: ProposalStatus,
  currentRevisionId: Id.nullable(),
  tags: z.array(z.string().max(50)).max(30),
  industry: z.string().max(100).nullable(),
  updatedAt: IsoDate,
  deletedAt: IsoDate.nullable(),
});
export type Proposal = z.infer<typeof Proposal>;

export const Revision = z.object({
  id: Id,
  proposalId: Id,
  /** 表示用の版番号（"3"、枝分かれは "4-B"）。サーバーが同期時に決める。端末で作った直後は null */
  label: z.string().max(20).nullable(),
  parentRevisionId: Id.nullable(),
  space: Space,
  layout: Layout,
  estimate: Estimate,
  occupancyFootprint: z.number().min(0).max(100),
  occupancyRequired: z.number().min(0).max(100),
  note: z.string().max(2_000),
  createdBy: Id,
  createdAt: IsoDate,
  /** 他の提案の版から流用したときの元の版 */
  createdFrom: Id.nullable(),
});
export type Revision = z.infer<typeof Revision>;

/* ===== 同期（docs/offline-sync.md） ===== */
export const SyncEntity = z.enum(["customer", "proposal", "revision"]);
export const SyncOp = z.discriminatedUnion("entity", [
  z.object({ opId: Id, entity: z.literal("customer"), action: z.enum(["upsert", "delete"]), data: Customer, baseUpdatedAt: IsoDate.nullable(), clientTs: IsoDate }),
  z.object({ opId: Id, entity: z.literal("proposal"), action: z.enum(["upsert", "delete"]), data: Proposal, baseUpdatedAt: IsoDate.nullable(), clientTs: IsoDate }),
  /** 版は追記だけ */
  z.object({ opId: Id, entity: z.literal("revision"), action: z.literal("create"), data: Revision, baseUpdatedAt: z.null(), clientTs: IsoDate }),
]);
export type SyncOp = z.infer<typeof SyncOp>;
export const SyncPushRequest = z.object({ deviceId: z.string().min(1).max(64), ops: z.array(SyncOp).max(500) });
export const SyncOpResult = z.object({
  opId: Id,
  result: z.enum(["applied", "duplicate", "conflict_branched", "conflict_field", "rejected"]),
  message: z.string().max(300).optional(),
  /** サーバーで確定した値（版番号・再計算した見積など） */
  serverData: z.unknown().optional(),
});
export const SyncPushResponse = z.object({ results: z.array(SyncOpResult), serverSeq: z.number().int() });

/** 取得は更新番号（serverSeq）より新しいものだけ。削除は deletedAt で配る */
export const SyncPullResponse = z.object({
  serverSeq: z.number().int(),
  hasMore: z.boolean(),
  makers: z.array(Maker),
  processes: z.array(Process),
  products: z.array(Product),
  patterns: z.array(Pattern),
  estimateRules: z.array(EstimateRules),
  customers: z.array(Customer),
  proposals: z.array(Proposal),
  revisions: z.array(Revision),
  /** 消えたマスタの ID（archived 以外の物理削除） */
  deleted: z.array(z.object({ entity: z.string(), id: Id })),
  /** true なら端末のデータを消してログアウトさせる（停止された利用者） */
  wipe: z.boolean(),
});
export type SyncPullResponse = z.infer<typeof SyncPullResponse>;

/* ===== 取り込み・AI ===== */
export const ImportSourceType = z.enum(["excel", "csv", "pdf", "image", "url"]);
export const ImportJob = z.object({
  id: Id,
  sourceType: ImportSourceType,
  sourceName: z.string().max(500),
  status: z.enum(["queued", "extracting", "done", "failed"]),
  rowsCreated: z.number().int(),
  rowsUpdated: z.number().int(),
  rowsWarning: z.number().int(),
  error: z.string().max(1_000).nullable(),
  createdBy: Id,
  createdAt: IsoDate,
});
export type ImportJob = z.infer<typeof ImportJob>;
export const Evidence = z.object({
  page: z.number().int().min(1).optional(),
  box: z.tuple([z.number(), z.number(), z.number(), z.number()]).optional(),
  cell: z.string().max(20).optional(),
  text: z.string().max(500).optional(),
});
export const ImportRow = z.object({
  id: Id,
  jobId: Id,
  productId: Id.nullable(),
  action: z.enum(["created", "updated", "unchanged", "skipped"]),
  extracted: z.record(z.string(), z.unknown()),
  evidence: z.record(z.string(), Evidence),
  checkedAt: IsoDate.nullable(),
  checkedBy: Id.nullable(),
});
export type ImportRow = z.infer<typeof ImportRow>;

export const AiProvider = z.enum(["gemini", "openai"]);
export const AiFeature = z.enum(["ocr", "text", "embedding", "reread"]);
export const AiSettings = z.object({
  keys: z.array(z.object({ provider: AiProvider, last4: z.string().max(4), status: z.enum(["ok", "invalid", "unchecked"]) })),
  features: z.array(z.object({ feature: AiFeature, provider: AiProvider, model: z.string().max(100) })),
  monthlyLimitJpy: Yen.nullable(),
  usage: z.array(z.object({ month: z.string().max(7), feature: AiFeature, provider: AiProvider, calls: z.number().int(), estimatedCostJpy: z.number() })),
});
export type AiSettings = z.infer<typeof AiSettings>;
export const PutAiKeyRequest = z.object({ provider: AiProvider, apiKey: z.string().min(10).max(500) });
export const PutAiFeaturesRequest = z.object({
  features: z.array(z.object({ feature: AiFeature, provider: AiProvider, model: z.string().min(1).max(100) })).max(10),
  monthlyLimitJpy: Yen.nullable(),
});

/** 文章で要望を反映（オンラインは AI、オフラインは規則）。AI はマスタにある ID しか返せない */
export const InterpretRequest = z.object({ text: z.string().min(1).max(2_000), context: z.object({ processIds: z.array(Id), productIds: z.array(Id), pattern: LayoutPattern }) });
export const InterpretResponse = z.object({
  reply: z.string().max(500),
  updates: z.object({
    spaceWidthMm: PositiveMm.optional(),
    spaceDepthMm: PositiveMm.optional(),
    pattern: LayoutPattern.optional(),
    removeProcessIds: z.array(Id).optional(),
    addProcessIds: z.array(Id).optional(),
    setProducts: z.record(Id, Id).optional(),
  }),
});

/* ===== API の共通 ===== */
export const ApiError = z.object({ error: z.object({ code: z.string(), message: z.string() }) });
export const VersionInfo = z.object({ sha: z.string(), deployedAt: IsoDate });
